import React, { useEffect, useMemo, useState } from 'react';
import { Pencil, Trash2, MoreVertical, Search, Plus } from 'lucide-react';
import useComponentVisible from '../UI/ComponentVisible';
import { useUserContext } from '../../contexts/UserContext';
import PlanModal from './PlanModal';
import DeletePlanModal from './DeletePlanModal';
import { formatDate } from '../../utils/converter';
import { getAllPlans, getOrgLevels } from '../../utils/APIs';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { Button, Input, AgTableCard } from '../UI';

const ManagePlan = () => {
  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
  const { content } = useContentfulContext();

  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [loading, setLoading] = useState(false);
  const [orgLevels, setOrgLevels] = useState([]);
  const [selectedAction, setSelectedAction] = useState('');
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const { userData } = useUserContext();
  const [gridApi, setGridApi] = useState(null);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await getAllPlans(userData?.token, userData?.selectedOrganization?.org_token);
      if (res.status) {
        setPlans(res.data);
      }
    } catch (error) {
      console.log("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgLevels = async () => {
    try {
        const res = await getOrgLevels(userData.token, userData?.selectedOrganization?.org_token);
        if (res.status) {
            setOrgLevels(res.data);
        }
    } catch (error) {
        console.error("Error fetching organization levels:", error);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchOrgLevels();
  }, []);

  const colDefs = [
    {
      headerName: content?.ADMIN_PLAN?.table_headers?.plan_name,
      field: "plan_name",
      sortable: true,
      filter: true,
      cellRenderer: ({ data }) => (
        <button
          type="button"
          className="text-left font-medium text-primary hover:text-primary-hover hover:underline cursor-pointer"
          onClick={() => {
            setSelectedPlan(data);
            setSelectedAction("edit");
          }}
        >
          {data.plan_name}
        </button>
      ),
      suppressMovable: true
    },
    {
      headerName: content?.ADMIN_PLAN?.table_headers?.plan_description,
      field: "plan_description",
      sortable: true,
      filter: true,
      suppressMovable: true
    },
    {
      headerName: content?.ADMIN_PLAN?.table_headers?.org_level,
      field: "org_level",
      cellRenderer: ({ data }) => (
        <span className="stride-badge-pill bg-purple-50 text-purple-700 border border-purple-200 text-xs font-semibold">
          Level {data.org_level}
        </span>
      ),
      sortable: true,
      filter: true,
      suppressMovable: true
    },
    {
      headerName: content?.ADMIN_PLAN?.table_headers?.price,
      field: "price",
      sortable: true,
      cellRenderer: ({ data }) => (
        <span className="font-semibold text-xs text-stride-heading">
          {data.price} {data.price_currency}
        </span>
      ),
      suppressMovable: true
    },
    {
      headerName: content?.ADMIN_PLAN?.table_headers?.discount,
      field: "discount",
      sortable: true,
      cellRenderer: ({ data }) => `${data.discount}%`,
      suppressMovable: true
    },
    {
      headerName: content?.ADMIN_PLAN?.table_headers?.duration,
      field: "duration",
      sortable: true,
      suppressMovable: true
    },
    {
      headerName: content?.ADMIN_PLAN?.table_headers?.ai_credits_limit,
      field: "ai_credits_limit",
      sortable: true,
      suppressMovable: true
    },
    {
      headerName: content?.ADMIN_PLAN?.table_headers?.ai_margin_percent,
      field: "ai_margin_percent",
      sortable: true,
      cellRenderer: ({ data }) => `${data.ai_margin_percent}%`,
      suppressMovable: true
    },
    {
      headerName: content?.ADMIN_PLAN?.table_headers?.is_custom_plan,
      field: "is_custom_plan",
      cellRenderer: ({ data }) => (
        <span className={`stride-badge-pill text-xs font-semibold ${data?.is_custom_plan ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-slate-100 text-slate-600'}`}>
          {data?.is_custom_plan ? "Custom" : "Standard"}
        </span>
      ),
      sortable: true,
      suppressMovable: true
    },
    {
      headerName: content?.ADMIN_PLAN?.table_headers?.is_topup,
      field: "is_topup",
      cellRenderer: ({ data }) => (
        <span className={`stride-badge-pill text-xs font-semibold ${data?.is_topup ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-slate-100 text-slate-600'}`}>
          {data?.is_topup ? "Top-up" : "Base"}
        </span>
      ),
      sortable: true,
      suppressMovable: true
    },
    {
      headerName: content?.ADMIN_PLAN?.table_headers?.created_at,
      field: "createdAt",
      sortable: true,
      cellRenderer: ({ data }) => (
        <span className="text-xs text-stride-muted tabular-nums">
          {formatDate(data?.createdAt)}
        </span>
      ),
      suppressMovable: true
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 120,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      cellRenderer: ({ data }) => (
        <div className="stride-table-actions">
          <button
            type="button"
            title="Edit Plan"
            className="stride-table-btn stride-table-btn-edit"
            onClick={() => {
              setSelectedPlan(data);
              setSelectedAction("edit");
            }}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            title="Delete Plan"
            className="stride-table-btn stride-table-btn-delete"
            onClick={() => {
              setSelectedPlan(data);
              setSelectedAction("delete");
            }}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            className="stride-table-btn actions"
            title="More options"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPlan(data);
              setIsComponentVisible(!isComponentVisible);
              const buttonRect = e.currentTarget.getBoundingClientRect();
              setMenuPosition({
                top: buttonRect.bottom + window.scrollY,
                left: buttonRect.left + window.scrollX,
              });
            }}
          >
            <MoreVertical className="w-3.5 h-3.5" />
          </button>
        </div>
      ),
    },
  ];

  const autoSizeStrategy = useMemo(() => ({
    type: "fitGridWidth",
    defaultMinWidth: 120,
    columnLimits: [
      { colId: "plan_name", minWidth: 160 },
      { colId: "plan_description", minWidth: 200 },
      { colId: "org_level", minWidth: 120 },
      { colId: "price", minWidth: 120 },
      { colId: "is_custom_plan", minWidth: 110 },
      { colId: "is_topup", minWidth: 100 },
      { colId: "discount", minWidth: 100 },
      { colId: "ai_credits_limit", minWidth: 120 },
      { colId: "ai_margin_percent", minWidth: 110 },
      { colId: "duration", minWidth: 120 },
      { colId: "createdAt", minWidth: 150 },
      { colId: "actions", minWidth: 120 }
    ],
  }), []);

  const onFilterTextBoxChanged = (event) => {
    const filterValue = (event.target.value || '').toLowerCase();
    gridApi.setFilterModel({
      plan_name: { type: 'contains', filter: filterValue }
    });
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  return (
    <>
      <main id="main" className="stride-page-shell flex flex-col space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <span className="stride-eyebrow">Administration</span>
            <div className="flex items-baseline gap-3">
              <h1 className="text-2xl font-bold tracking-tight text-stride-heading">
                {content?.ADMIN_PLAN?.title}
              </h1>
              <span className="text-xs text-stride-muted font-normal">
                {plans.length} {plans.length === 1 ? 'plan' : 'plans'}
              </span>
            </div>
          </div>

          <Button
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => {
              setSelectedAction("create");
              setSelectedPlan(null);
            }}
          >
            {content?.ADMIN_PLAN?.cta_text?.create_plan}
          </Button>
        </div>

        <AgTableCard
          header={
            <div className="w-full sm:w-80">
              <Input
                placeholder="Search plans..."
                onChange={onFilterTextBoxChanged}
                prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                clearable
              />
            </div>
          }
          tableId="manage-plan-table"
          loading={loading}
          rowData={plans}
          columnDefs={colDefs}
          autoSizeStrategy={autoSizeStrategy}
          onGridReady={onGridReady}
        >
          {isComponentVisible && (
            <div
              ref={ref}
              className="absolute z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none py-1"
              style={{ top: menuPosition.top, left: Math.max(10, menuPosition.left - 180) }}
            >
              <div className="py-1">
                <a
                  href="#"
                  className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsComponentVisible(false);
                    setSelectedAction("edit");
                  }}
                >
                  {content?.ADMIN_PLAN?.more_info?.edit}
                </a>
                <a
                  href="#"
                  className="text-rose-600 block px-4 py-2 text-sm hover:bg-gray-100"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsComponentVisible(false);
                    setSelectedAction("delete");
                  }}
                >
                  {content?.ADMIN_PLAN?.more_info?.delete}
                </a>
              </div>
            </div>
          )}
        </AgTableCard>
      </main>

      {selectedAction === "create" && <PlanModal setSelectedAction={setSelectedAction} getPlans={fetchPlans} orgLevels={orgLevels} />}
      {selectedAction === "edit" && <PlanModal setSelectedAction={setSelectedAction} selectedPlan={selectedPlan} getPlans={fetchPlans} orgLevels={orgLevels} />}
      {selectedAction === "delete" && <DeletePlanModal setSelectedAction={setSelectedAction} selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan} getPlans={fetchPlans} />}
    </>
  );
};

export default ManagePlan;